/* eslint-disable react-refresh/only-export-components -- standard context pattern: provider and hook co-located */
/**
 * Carries each action's DISPLAY number — its position in the rendered list —
 * from the one component that can see the whole list down to the cards and
 * rows that show it.
 *
 * The numbering is proposal-wide and derived from render order
 * (`buildActionDisplayNumbering`), so it must be computed above the groups.
 * The cards that render it sit five layers below — MultiActionTimeline →
 * ActionGroupRenderer → ActionAccordionItem / MergedAccordionEntry →
 * ActionAccordion → ActionCardShell — so this is a context rather than a
 * prop threaded through every one of them.
 *
 * Optional by design: a surface rendering an action outside a proposal list
 * (a story, a single-action preview) has no list to be a position in, so the
 * hook returns undefined and the caller shows no number.
 */

import { createContext, type ReactNode, useContext } from 'react';

import type { ActionDisplayNumbering } from '../utils/actionDisplayNumbers';

const EMPTY_NUMBERING: ActionDisplayNumbering = {
  numbers: new Map(),
  total: 0,
};

const ActionDisplayNumberContext =
  createContext<ActionDisplayNumbering>(EMPTY_NUMBERING);

export function ActionDisplayNumberProvider({
  numbering,
  children,
}: {
  numbering: ActionDisplayNumbering;
  children: ReactNode;
}) {
  return (
    <ActionDisplayNumberContext value={numbering}>
      {children}
    </ActionDisplayNumberContext>
  );
}

/**
 * The 1-based position of `actionId` in the rendered list, or undefined when
 * the action isn't part of a numbered list.
 */
export function useActionDisplayNumber(actionId: number): number | undefined {
  return useContext(ActionDisplayNumberContext).numbers.get(actionId);
}

/**
 * How many cards/rows the proposal renders. Callers gate the numbers on this
 * (> 1) so a single-card proposal isn't labelled "1." — and, critically, so
 * the decision is made once for the whole list instead of per group, which
 * would hide the "1." and start a later group at "2.".
 */
export function useRenderedActionCount(): number {
  return useContext(ActionDisplayNumberContext).total;
}
